import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@/components/error';
import FeaturePreviewAnnouncement from '@/components/feature-preview-announcement';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import Loading from '../onboardings-shared/components/loading';
import { CURRENT_ONBOARDING_ID, ONBOARDINGS_ID } from '../onboardings-shared/constants';
import useSelectedOnboarding from '../onboardings-shared/hooks/use-selected-onboarding';
import Section from '../section';
import CurrentData from './components/current-data';
import OnboardingData from './components/onboarding-data';
import OnboardingTabs from './components/onboarding-tabs';
import { CURRENT_TAB_ID, ONBOARDING_PANEL_ID } from './constants';
import useIsStuck from './hooks/use-is-stuck';
import useKeepSectionInView from './hooks/use-keep-section-in-view';
const Onboardings = () => {
  const $ = _c(46);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "onboardings"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const {
    kind
  } = useEntityContext();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      hasCurrentTab: true
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    onboardings,
    selectedOnboarding,
    isCurrentSelected,
    isPagingToTarget,
    setOnboardingId
  } = useSelectedOnboarding(t1);
  const {
    sentinelRef,
    isStuck
  } = useIsStuck();
  const selectedTabId = isCurrentSelected ? CURRENT_ONBOARDING_ID : selectedOnboarding?.id;
  const {
    stripRef,
    panelRef,
    rememberSectionInView
  } = useKeepSectionInView(selectedTabId);
  let t2;
  if ($[2] !== rememberSectionInView || $[3] !== selectedTabId || $[4] !== setOnboardingId) {
    t2 = id => {
      if (id === selectedTabId) {
        return;
      }
      rememberSectionInView();
      setOnboardingId(id);
    };
    $[2] = rememberSectionInView;
    $[3] = selectedTabId;
    $[4] = setOnboardingId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleSelect = t2;
  let t3;
  if ($[6] !== t) {
    t3 = t(_temp);
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== hasNextPage || $[9] !== onboardings.length) {
    t4 = onboardings.length ? <span className="text-body-2 text-tertiary">({hasNextPage ? `${onboardings.length}+` : onboardings.length})</span> : null;
    $[8] = hasNextPage;
    $[9] = onboardings.length;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t(_temp2);
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FeaturePreviewAnnouncement preview="onboardings_strip" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isPending) {
    t7 = isPending && <Loading />;
    $[14] = isPending;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== error) {
    t8 = error && <ErrorComponent error={error} />;
    $[16] = error;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== error || $[19] !== isPending || $[20] !== kind || $[21] !== selectedOnboarding || $[22] !== t) {
    t9 = !isPending && !error && !selectedOnboarding && <div className="flex flex-col gap-4"><p className="text-body-2 text-primary">{t(_temp3, {
          kind
        })}</p><CurrentData /></div>;
    $[18] = error;
    $[19] = isPending;
    $[20] = kind;
    $[21] = selectedOnboarding;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== fetchNextPage || $[25] !== handleSelect || $[26] !== hasNextPage || $[27] !== isCurrentSelected || $[28] !== isFetchingNextPage || $[29] !== isPagingToTarget || $[30] !== isStuck || $[31] !== onboardings || $[32] !== panelRef || $[33] !== selectedOnboarding || $[34] !== selectedTabId || $[35] !== sentinelRef || $[36] !== stripRef) {
    t10 = selectedOnboarding && <div className="flex flex-col"><div ref={sentinelRef} className="h-px" /><div ref={stripRef} className={cx("z-popover -mx-1 px-1 pt-2 pb-2 bg-primary border-b border-transparent md:sticky", {
        "md:top-0": !isStuck,
        "md:-top-13 md:-mt-7 md:pt-9 md:border-tertiary": isStuck
      })}><OnboardingTabs onboardings={onboardings} selectedId={selectedTabId ?? selectedOnboarding.id} onSelect={handleSelect} onLoadMore={fetchNextPage} hasMore={hasNextPage} isLoadingMore={isFetchingNextPage} /></div>{isPagingToTarget ? <div className="mt-3"><Loading /></div> : <div ref={panelRef} role="tabpanel" id={ONBOARDING_PANEL_ID} aria-labelledby={isCurrentSelected ? CURRENT_TAB_ID : selectedOnboarding.id} className="mt-3 md:[&_*]:scroll-mt-32">{isCurrentSelected ? <CurrentData /> : <OnboardingData key={selectedOnboarding.id} onboarding={selectedOnboarding} />}</div>}</div>;
    $[24] = fetchNextPage;
    $[25] = handleSelect;
    $[26] = hasNextPage;
    $[27] = isCurrentSelected;
    $[28] = isFetchingNextPage;
    $[29] = isPagingToTarget;
    $[30] = isStuck;
    $[31] = onboardings;
    $[32] = panelRef;
    $[33] = selectedOnboarding;
    $[34] = selectedTabId;
    $[35] = sentinelRef;
    $[36] = stripRef;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t11 = <Section title={t3} titleSuffix={t4} id={ONBOARDINGS_ID} aria-label={t5}>{t6}{t7}{t8}{t9}{t10}</Section>;
    $[38] = t10;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
};
export default Onboardings;
function _temp($) {
  return $.title;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.noOnboardings;
}